/**
 * The plan pass output — what the model returns *before* any Vue is written.
 *
 * This is deliberately a description of a site, not a site. Separating "decide
 * what the site is" from "write the components" buys three things:
 *
 *   1. The plan is small, cheap, and fast, so it can be shown to the user while
 *      the expensive pass runs. Latency stops feeling like a hang.
 *   2. Structure is enforced here, once, by a schema — rather than being
 *      re-derived from generated markup afterwards.
 *   3. Palette, typography and routing never pass through the model's markup at
 *      all. They arrive as data and are rendered by the scaffold.
 */
import { z } from 'zod';
// ── Typography ───────────────────────────────────────────────────────────────
/**
 * A closed set of font stacks, and no way to add one.
 *
 * Every stack resolves to fonts already on the device. A generated site never
 * fetches a webfont: the build worker has no network, a third-party font request
 * is a privacy and latency problem for the visitor, and a failed one is a
 * visible layout shift. The model picks a *pairing* from this list; it does not
 * name typefaces.
 */
export const FONT_STACKS = {
    'system-sans': `ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
    'system-serif': `ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif`,
    'system-mono': `ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace`,
    'humanist-sans': `'Optima', Candara, 'Gill Sans', 'Gill Sans MT', 'Trebuchet MS', ui-sans-serif, sans-serif`,
    'grotesque-sans': `'Helvetica Neue', Helvetica, 'Arial Nova', Arial, ui-sans-serif, sans-serif`,
    'transitional-serif': `'Iowan Old Style', 'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, ui-serif, serif`,
    'slab-serif': `Rockwell, 'Rockwell Nova', 'Roboto Slab', 'DejaVu Serif', Georgia, ui-serif, serif`,
    'geometric-sans': `Futura, 'Avenir Next', Avenir, 'Century Gothic', ui-sans-serif, sans-serif`,
};
export const fontStackSchema = z.enum(Object.keys(FONT_STACKS));
export const fontPairingSchema = z.object({
    /** Headlines and display type. */
    display: fontStackSchema,
    /** Body copy. May equal `display` — a single-family site is a valid choice. */
    body: fontStackSchema,
});
// ── Palette ──────────────────────────────────────────────────────────────────
const hexColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex colour like #1e1b4b');
/**
 * Four roles, not a full scale.
 *
 * The scaffold derives an 11-step ramp from each of these (see
 * `packages/site-template/scripts/theme.mjs`). Asking the model for the ramp
 * instead would mean eleven chances to produce a muddy or inconsistent step, and
 * would put colour maths in generated output where it cannot be reviewed.
 */
export const paletteSchema = z.object({
    /** Primary. Buttons, links, the thing the eye lands on. */
    brand: hexColorSchema,
    /** Secondary, for highlights and small emphasis. May match `brand`. */
    accent: hexColorSchema,
    /** Page background. Usually near-white or near-black. */
    surface: hexColorSchema,
    /** Body text. Must have real contrast against `surface`. */
    ink: hexColorSchema,
});
// ── Sections ─────────────────────────────────────────────────────────────────
/**
 * The section vocabulary.
 *
 * A closed list is what makes the generate pass predictable: each kind maps to a
 * known layout shape and a known set of copy fields, so "write this section"
 * is a small, well-specified task rather than an open invitation.
 */
export const SECTION_KINDS = [
    'hero',
    'features',
    'stats',
    'logos',
    'testimonial',
    'pricing',
    'faq',
    'gallery',
    'team',
    'timeline',
    'menu',
    'hours',
    'contact',
    'cta',
    'prose',
];
export const sectionKindSchema = z.enum(SECTION_KINDS);
/**
 * One repeated element inside a section — a feature, a plan, a menu line.
 *
 * Kept generic on purpose. Fifteen bespoke item shapes would be fifteen ways for
 * the model to pick the wrong one; one shape with optional fields is a target it
 * can hit every time.
 */
/**
 * Length caps are guard-rails against runaway output, not editorial limits.
 *
 * They were tuned against the hand-written reference sites, whose longest item
 * title is 43 characters — and a real model wrote past 120 on its first attempt,
 * which failed the whole generation and cost real money for nothing. A verbose
 * headline is a mildly worse site; a rejected plan is no site at all, so these
 * now sit far above anything sensible and brevity is asked for in the prompt
 * where it belongs.
 */
export const sectionItemSchema = z.object({
    title: z.string().min(1).max(200),
    body: z.string().max(800).optional(),
    /** Price, stat value, time — whatever the kind wants in the emphasised slot. */
    value: z.string().max(120).optional(),
    /** Lucide icon name in PascalCase, e.g. "Clock". Ignored by kinds without icons. */
    icon: z
        .string()
        .regex(/^[A-Z][A-Za-z0-9]{1,39}$/)
        .optional(),
});
export const sectionSchema = z.object({
    kind: sectionKindSchema,
    /** The section's headline. Empty string means "no headline for this one". */
    headline: z.string().max(240),
    /** Supporting paragraph beneath the headline. */
    body: z.string().max(1_200).optional(),
    items: z.array(sectionItemSchema).max(12).optional(),
    /** Call-to-action label, when the kind supports one. */
    ctaLabel: z.string().max(40).optional(),
    /** Where the CTA points. A route path, or an anchor like "#contact". */
    ctaHref: z.string().max(120).optional(),
});
// ── Pages ────────────────────────────────────────────────────────────────────
export const pagePlanSchema = z.object({
    /** Vue Router path. Same grammar the router renderer accepts. */
    path: z
        .string()
        .regex(/^\/(?:[A-Za-z0-9\-_]+|:[A-Za-z][A-Za-z0-9]*)?(?:\/(?:[A-Za-z0-9\-_]+|:[A-Za-z][A-Za-z0-9]*))*$/, 'must look like /, /about or /services/:slug'),
    name: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/, 'kebab-case route name'),
    /** PascalCase component name — becomes src/views/<view>.vue. */
    view: z.string().regex(/^[A-Z][A-Za-z0-9]{1,39}$/),
    title: z.string().min(1).max(140),
    description: z.string().min(1).max(160),
    sections: z.array(sectionSchema).min(1).max(10),
});
// ── The blueprint ────────────────────────────────────────────────────────────
export const SITE_TYPES = [
    'saas',
    'portfolio',
    'local-business',
    'agency',
    'restaurant',
    'event',
    'nonprofit',
    'personal',
    'product',
    'community',
];
export const TONES = [
    'warm',
    'direct',
    'editorial',
    'playful',
    'technical',
    'refined',
    'civic',
];
export const blueprintSchema = z.object({
    siteType: z.enum(SITE_TYPES),
    /** The organisation or person the site is for. */
    name: z.string().min(1).max(60),
    tagline: z.string().min(1).max(200),
    /** Steers voice in the generate pass. Not rendered anywhere directly. */
    tone: z.enum(TONES),
    palette: paletteSchema,
    fonts: fontPairingSchema,
    /** Site-wide navigation labels, in order. Each must match a page path. */
    navigation: z.array(z.object({ label: z.string().min(1).max(24), path: z.string() })).max(6),
    pages: z.array(pagePlanSchema).min(1).max(8),
    /** Meta description for the site as a whole. */
    description: z.string().min(1).max(160),
});
/**
 * Cross-field checks the schema alone cannot express.
 *
 * Returned as messages rather than thrown, because a blueprint that fails these
 * is repairable — the model gets the list back and fixes it, exactly like the
 * file-level repair pass.
 */
export function validateBlueprint(blueprint) {
    const problems = [];
    const paths = new Set(blueprint.pages.map((page) => page.path));
    const views = new Set();
    const names = new Set();
    if (!paths.has('/'))
        problems.push('there must be a page at "/"');
    for (const page of blueprint.pages) {
        if (views.has(page.view))
            problems.push(`two pages both use the view "${page.view}"`);
        views.add(page.view);
        if (names.has(page.name))
            problems.push(`two pages both use the route name "${page.name}"`);
        names.add(page.name);
    }
    for (const entry of blueprint.navigation) {
        if (!paths.has(entry.path)) {
            problems.push(`navigation links to "${entry.path}", which is not a page`);
        }
    }
    // A CTA pointing at a route that does not exist renders a dead link, which is
    // exactly the kind of defect that looks fine in a screenshot and is obvious
    // the moment someone clicks.
    for (const page of blueprint.pages) {
        for (const section of page.sections) {
            const href = section.ctaHref;
            if (href && href.startsWith('/') && !paths.has(href)) {
                problems.push(`a CTA on "${page.path}" links to "${href}", which is not a page`);
            }
        }
    }
    return problems;
}
//# sourceMappingURL=blueprint.js.map